import type {
  ProductBacklogApplyResult,
  ProductBacklogSectionKind,
  ProductBacklogWriteBlockReason,
} from '@cortex/domain-ac-mens-hub-api';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronRight,
  Clock,
  OctagonAlert,
  X,
} from 'lucide-react';
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { PendingMove } from '../../lib/product-backlog-arrange.js';
import { formatReadAt } from '../../lib/product-backlog-view.js';

/**
 * 直前の反映・元に戻すの結果（failed は API に届かなかった）。
 * @graph-connects none
 */
export type BandResult =
  | { action: 'apply' | 'undo'; result: ProductBacklogApplyResult }
  | { action: 'failed' };

/**
 * 帯の色。
 * @graph-connects none
 */
type Tone = 'plain' | 'ok' | 'warn' | 'error' | 'muted';

/** 色ごとの見た目。 @graph-connects none */
const TONE_CLASS: Record<Tone, string> = {
  plain: 'bg-[var(--color-bg-surface)] text-[var(--color-text-primary)]',
  ok: 'bg-[var(--color-bg-surface)] text-[var(--color-success)]',
  warn: 'border-t-amber-500/40 bg-amber-50 text-amber-800 dark:bg-amber-500/10 dark:text-amber-300',
  error: 'border-t-[var(--color-error)]/40 bg-[var(--color-error-muted)] text-[var(--color-error)]',
  muted: 'bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)]',
};

/**
 * 下の帯の引数。
 * - moves: 未反映の並べ替え（動かした札）。draftSince は下書きを作った日時
 * - block: 今が反映しない時間帯なら、反映できるようになる時刻と理由
 * - draftStale: 下書きを作った後にスプシの並びが変わり、下書きを当てられなかった（変わった案件の題名と残りの件数）
 * @graph-connects none
 */
export interface ProductBacklogBandProps {
  moves: readonly PendingMove[];
  draftSince: string | null;
  block: { resumeAt: string; reason: ProductBacklogWriteBlockReason } | null;
  switchOn: boolean;
  draftStale: { title: string; more: number } | null;
  working: boolean;
  saving: boolean;
  result: BandResult | null;
  now: Date;
  sheetUrl: string;
  onApply: () => void;
  onDiscard: () => void;
  onUndo: (batchId: string) => void;
  onOpenRecords: () => void;
  onReload: () => void;
  onCloseResult: () => void;
}

/**
 * 区分の中の位置の書き方（未振り分けは位置を書かない）。
 * @graph-connects none
 */
function placeOf(
  t: (key: string, params?: Record<string, string>) => string,
  kind: ProductBacklogSectionKind,
  rank: number,
): string {
  if (kind === 'unassigned') return t('productBacklog.band.unassigned');
  return t('productBacklog.band.place', {
    section: t(`productBacklog.sections.${kind}.name`),
    rank: String(rank),
  });
}

/**
 * 帯の置き場所（画面の下に固定し、横は一覧の列に合わせる）。左のメニューの開け閉めで列の幅が変わっても追う。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 画面の下に固定する帯の横の位置と幅を、一覧の列の位置と幅に合わせて追い続ける
 * @graph-connects none
 */
function useColumnBox(anchor: { current: HTMLElement | null }): { left: number; width: number } {
  const [box, setBox] = useState({ left: 0, width: 0 });
  useLayoutEffect(() => {
    const element = anchor.current;
    if (element === null) return undefined;
    const measure = () => {
      const rect = element.getBoundingClientRect();
      setBox({ left: rect.left, width: rect.width });
    };
    measure();
    window.addEventListener('resize', measure);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(element);
    return () => {
      window.removeEventListener('resize', measure);
      observer?.disconnect();
    };
  }, [anchor]);
  return box;
}

/**
 * 帯1本の枠（文言と右のボタン、必要なら下に一覧）。画面の下に固定する（body に出す）。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 案件一覧の下に固定する帯1本を、色・文言・ボタン・中身の一覧で出す
 * @graph-connects useColumnBox [calls] 帯の横の位置と幅
 */
function BandFrame({
  tone,
  children,
  actions,
  list,
}: {
  tone: Tone;
  children: ReactNode;
  actions: ReactNode;
  list?: ReactNode;
}) {
  const anchor = useRef<HTMLDivElement>(null);
  const box = useColumnBox(anchor);
  return (
    <>
      <div ref={anchor} aria-hidden="true" className="h-0 w-full" />
      {createPortal(
        <div
          role="status"
          data-band-tone={tone}
          style={{ left: box.left, width: box.width }}
          className={`fixed bottom-0 z-40 hidden border-t border-[var(--color-border)] shadow-[0_-6px_16px_-6px_rgb(0_0_0/0.10)] md:block ${TONE_CLASS[tone]}`}
        >
          <div className="flex flex-wrap items-center gap-3 px-6 py-3">
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2 text-sm">
              {children}
            </div>
            <div className="flex items-center gap-2">{actions}</div>
          </div>
          {list}
        </div>,
        // 一覧の枠は動きの効果（transform）を持ち、その中では fixed が画面に固定されないので body に出す
        document.body,
      )}
    </>
  );
}

/**
 * 案件一覧の下の帯（動かす人の画面にだけ、下書きか結果があるときに出す。スマホ幅では出さない）。
 * 反映できる・待つ時間（書き写し・12時台）・反映中・反映した・見送った（並びが変わっていた）と、止めた（ずれ）・
 * 失敗（混雑・権限）・ずれた（反映した後）・ほかで反映中・緊急停止・元に戻した・戻せないを出し分ける。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 案件の並べ替えの下書きと反映の結果を一覧の下の帯で出し、スプシに反映・やめる・元に戻す・記録を見る・最新を読み込むを受け付ける。反映しない時間帯は反映できる時刻を出して押せなくする
 * @graph-connects BandFrame [calls] 帯の枠
 * @graph-connects placeOf [calls] 区分の中の位置
 * @graph-connects formatReadAt [calls] 下書きを作った時刻
 */
export function ProductBacklogBand(props: ProductBacklogBandProps) {
  const { t } = useTranslation();
  const [peek, setPeek] = useState(false);
  const { moves, result } = props;

  const close = (
    <button
      type="button"
      aria-label={t('productBacklog.band.close')}
      onClick={props.onCloseResult}
      className="inline-flex h-7 w-7 items-center justify-center rounded-md hover:bg-[var(--color-bg-hover)]"
    >
      <X className="h-4 w-4" aria-hidden="true" />
    </button>
  );
  const records = (
    <Button size="sm" variant="ghost" onClick={props.onOpenRecords}>
      {t('productBacklog.band.records')}
    </Button>
  );
  const reload = (
    <Button size="sm" onClick={props.onReload}>
      {t('productBacklog.reload')}
    </Button>
  );
  const applyButtons = (
    <>
      <Button size="sm" variant="ghost" onClick={props.onDiscard} disabled={props.working}>
        {t('productBacklog.band.discard')}
      </Button>
      <Button
        size="sm"
        onClick={props.onApply}
        disabled={props.block !== null || props.working || props.saving}
      >
        {t('productBacklog.band.apply')}
      </Button>
    </>
  );
  const peekButton = (
    <button
      type="button"
      aria-expanded={peek}
      onClick={() => {
        setPeek((current) => !current);
      }}
      className="inline-flex items-center gap-0.5 text-[13px] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
    >
      {peek ? (
        <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
      ) : (
        <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
      )}
      {t('productBacklog.band.peek')}
    </button>
  );
  const peekList = peek ? (
    <ul className="m-0 grid list-none gap-1 px-6 pb-3 text-[13px] text-[var(--color-text-secondary)]">
      {moves.map((move) => (
        <li key={move.card.rowNumber}>
          <b className="font-semibold text-[var(--color-text-primary)]">
            {t('productBacklog.band.moveTitle', { title: move.card.title ?? move.card.key ?? '' })}
          </b>{' '}
          {t('productBacklog.band.moveLine', {
            from: placeOf(t, move.from.kind, move.from.rank),
            to:
              move.from.kind === move.to.kind
                ? t('productBacklog.band.rank', { rank: String(move.to.rank) })
                : placeOf(t, move.to.kind, move.to.rank),
          })}
        </li>
      ))}
    </ul>
  ) : undefined;
  const pending = (message?: ReactNode, tone: Tone = 'plain') => (
    <BandFrame tone={tone} actions={applyButtons} list={peekList}>
      {message}
      <span>
        {t('productBacklog.band.pending')}{' '}
        <b className="font-bold tabular-nums">
          {t('productBacklog.count', { n: String(moves.length) })}
        </b>
        {props.draftSince !== null && props.block === null && (
          <span className="text-xs tabular-nums text-[var(--color-text-muted)]">
            {t('productBacklog.band.since', { time: formatReadAt(props.draftSince, props.now) })}
          </span>
        )}
      </span>
      {peekButton}
      {props.block !== null && (
        <span className="inline-flex items-center gap-1 text-xs text-[var(--color-text-muted)]">
          <Clock className="h-3.5 w-3.5" aria-hidden="true" />
          <b className="font-bold text-[var(--color-text-primary)]">
            {t('productBacklog.band.resumeAt', {
              time: formatReadAt(props.block.resumeAt, props.now),
            })}
          </b>
          {t(`productBacklog.band.resumeReason.${props.block.reason}`)}
        </span>
      )}
    </BandFrame>
  );

  if (!props.switchOn) {
    return (
      <BandFrame tone="muted" actions={null}>
        {t('productBacklog.band.switchOff')}
      </BandFrame>
    );
  }
  if (props.working) {
    return (
      <BandFrame
        tone="plain"
        actions={
          <Button size="sm" disabled>
            {t('productBacklog.band.applying')}
          </Button>
        }
      >
        <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-[var(--color-border)] border-t-[var(--color-accent-primary)]" />
        {t('productBacklog.band.checking')}
      </BandFrame>
    );
  }
  if (result !== null) {
    if (result.action === 'failed') {
      return pending(<span>{t('productBacklog.band.failed')}</span>, 'warn');
    }
    const r = result.result;
    switch (r.outcome) {
      case 'applied':
        return (
          <BandFrame
            tone="ok"
            actions={
              <>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    props.onUndo(r.batchId);
                  }}
                >
                  {t('productBacklog.band.undo')}
                </Button>
                {records}
                {close}
              </>
            }
          >
            <Check className="h-4 w-4" aria-hidden="true" />
            <b className="font-bold">{t('productBacklog.band.applied', { n: String(r.count) })}</b>
            <span className="text-[var(--color-text-secondary)]">
              {t('productBacklog.band.appliedSub')}
            </span>
          </BandFrame>
        );
      case 'undone':
        return (
          <BandFrame tone="plain" actions={close}>
            {t('productBacklog.band.undone', { n: String(r.count) })}
          </BandFrame>
        );
      case 'stale':
        if (result.action === 'undo') {
          return (
            <BandFrame
              tone="warn"
              actions={
                <>
                  {records}
                  {close}
                </>
              }
            >
              <AlertTriangle className="h-4 w-4" aria-hidden="true" />
              {t('productBacklog.band.undoStale')}
            </BandFrame>
          );
        }
        return (
          <BandFrame tone="warn" actions={reload}>
            <AlertTriangle className="h-4 w-4" aria-hidden="true" />
            {r.changed.length === 0
              ? t('productBacklog.band.staleUnknown')
              : t('productBacklog.band.stale', {
                  title: r.changed[0]?.title ?? r.changed[0]?.key ?? '',
                  more: String(r.changed.length - 1),
                })}
          </BandFrame>
        );
      case 'drift_blocked':
        return pending(
          <span className="inline-flex items-center gap-1 font-medium text-[var(--color-error)]">
            <OctagonAlert className="h-4 w-4" aria-hidden="true" />
            {t('productBacklog.band.driftBlocked', {
              section: r.sectionKinds
                .map((kind) => t(`productBacklog.sections.${kind}.name`))
                .join('・'),
            })}
          </span>,
          'error',
        );
      case 'drifted':
      case 'unverified':
        return (
          <BandFrame
            tone="error"
            actions={
              <>
                {records}
                <a
                  href={props.sheetUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm font-medium underline"
                >
                  {t('productBacklog.band.openSheet')}
                </a>
                {close}
              </>
            }
          >
            <OctagonAlert className="h-4 w-4" aria-hidden="true" />
            {t(`productBacklog.band.${r.outcome}`)}
          </BandFrame>
        );
      case 'draft_outdated':
      case 'no_draft':
        return (
          <BandFrame tone="warn" actions={reload}>
            {t('productBacklog.band.outdated')}
          </BandFrame>
        );
      case 'undo_unavailable':
        return (
          <BandFrame tone="muted" actions={close}>
            {t('productBacklog.band.undoUnavailable')}
          </BandFrame>
        );
      case 'blocked_window':
      case 'switch_off':
      case 'busy':
      case 'layout_changed':
      case 'forbidden':
      case 'unavailable':
        if (moves.length > 0) {
          return pending(<span>{t(`productBacklog.band.outcome.${r.outcome}`)}</span>, 'warn');
        }
        return (
          <BandFrame tone="warn" actions={close}>
            {t(`productBacklog.band.outcome.${r.outcome}`)}
          </BandFrame>
        );
      default: {
        const unreachable: never = r;
        return unreachable;
      }
    }
  }
  if (props.draftStale !== null) {
    return (
      <BandFrame tone="warn" actions={reload}>
        <AlertTriangle className="h-4 w-4" aria-hidden="true" />
        {t('productBacklog.band.draftStale', {
          title: props.draftStale.title,
          more: String(props.draftStale.more),
        })}
      </BandFrame>
    );
  }
  if (moves.length > 0) return pending();
  return null;
}
